/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/**
 * `ScribbleBar` - the single "redacted" visual primitive.
 *
 * Renders an inline hand-drawn scribble that stands in for redacted content.
 * The stored text/ADF is already masked by the backend; this component only
 * makes the mask look intentional, branded, and accessible.
 *
 * Behavior (goal state, from Jono's PoC + the shipped Rovo Chat token):
 *   - Several SVG wavy strokes drawn left-to-right on mount (pen-scribble motion).
 *   - Respects `prefers-reduced-motion`: strokes render statically, no animation.
 *   - On hover/focus the strokes recolor to `color.background.accent.gray.bolder`
 *     and a Guard icon fades in as an affordance that the span is an intentional
 *     redaction. The darker fill keeps the icon legible on top of the strokes.
 *   - Wrapped in a two-line tooltip: "Redacted by your organization. / Powered
 *     by Atlassian Guard."
 *   - Always carries an accessible label so screen readers announce it.
 *
 * Ported from the Rovo Chat redaction token. Tuning lives in `./params` and the
 * stroke geometry in `./geometry`.
 */

import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { GuardIcon } from '../guard-icon';
import { messages } from '../messages';
import type { RedactionBackground } from '../types';
import { EFFECTIVE_STROKE_WIDTH, generateStrokes } from './geometry';
import { PARAMS } from './params';

export type ScribbleBarProps = {
	/** Surface background hint. Reserved for future per-surface tuning. */
	background?: RedactionBackground;
	/** Delay (ms) before the draw animation starts. Enables a cascade across many bars. */
	delay?: number;
	/** Accessible label override. Defaults to the localized "Redacted content". */
	label?: string;
	/**
	 * Legacy visual-variant hint from the 0.2.x published API. The bar now always
	 * draws its strokes and automatically respects `prefers-reduced-motion`, so
	 * this no longer selects a variant. Accepted (and ignored) so existing
	 * `variant="animated" | "static"` call sites keep compiling without a breaking
	 * change; new call sites should omit it.
	 */
	variant?: 'animated' | 'static';
};

const styles = cssMap({
	container: {
		// Reset native <button> chrome so the bar keeps its inline scribble look
		// while remaining a real, keyboard-focusable interactive element.
		appearance: 'none',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		border: 'none',
		backgroundColor: 'transparent',
		color: token('color.text'),
		position: 'relative',
		// SIZE CONTRACT: these box properties (`display`, `height`, `aspectRatio`,
		// `verticalAlign`, `borderRadius`) define the bar's inline footprint.
		// `RedactionPlaceholder` in `./placeholder.tsx` reserves this exact footprint
		// while the bar is still loading, so it duplicates the same values. Any change
		// to the bar's size here MUST be mirrored there or the placeholder-to-bar swap
		// will cause a layout shift.
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '1.125rem',
		aspectRatio: '3.4 / 1',
		verticalAlign: 'middle',
		cursor: 'default',
		// Redacted content must not be selectable/copyable.
		userSelect: 'none',
		borderRadius: token('radius.xsmall'),
		// No hover outline; hover only recolors the strokes and reveals the icon.
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
			outlineOffset: token('space.025'),
		},
	},
	svg: {
		position: 'absolute',
		top: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
		overflow: 'visible',
		pointerEvents: 'none',
		// Paths inherit this, so hover recolors with one transition.
		stroke: 'currentColor',
		transitionDuration: '150ms',
		transitionProperty: 'stroke',
		transitionTimingFunction: 'ease',
	},
	svgActive: {
		// Backdrop for the Guard icon, hence a background token.
		stroke: token('color.background.accent.gray.bolder'),
	},
	icon: {
		display: 'inline-flex',
		position: 'relative',
		zIndex: 1,
		// GuardIcon has no size below 16px, so scale to 12px; the lift puts its
		// center on the editor lozenge. Keep in step with PARAMS.iconSize/iconOffset.
		transform: 'translateY(-1.425px) scale(0.75)',
		opacity: 0,
		transitionDuration: '150ms',
		transitionProperty: 'opacity',
		transitionTimingFunction: 'ease',
	},
	iconActive: {
		opacity: 1,
	},
});

function prefersReducedMotion(): boolean {
	return (
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches
	);
}

/**
 * Measures the container and drives the sequential stroke draw animation.
 * Ported from the Rovo Chat redaction token.
 */
function useScribbleAnimationInternal(activationDelay: number): {
	containerRef: React.MutableRefObject<HTMLButtonElement | null>;
	pathData: string[];
	pathRefs: React.MutableRefObject<(SVGPathElement | null)[]>;
	svgDims: { h: number; w: number } | null;
} {
	const containerRef = useRef<HTMLButtonElement | null>(null);
	const pathRefs = useRef<(SVGPathElement | null)[]>([]);
	const [pathData, setPathData] = useState<string[]>([]);
	const [svgDims, setSvgDims] = useState<{ h: number; w: number } | null>(null);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}
		const rafId = requestAnimationFrame(() => {
			const { width, height } = el.getBoundingClientRect();
			const w = width || el.offsetWidth;
			const h = height || el.offsetHeight;
			if (w > 0 && h > 0) {
				setSvgDims({ w, h });
				setPathData(generateStrokes(w, h));
			}
		});
		return () => cancelAnimationFrame(rafId);
	}, []);

	useEffect(() => {
		if (!pathData.length || !svgDims) {
			return;
		}

		const paths = pathRefs.current.filter((p): p is SVGPathElement => p !== null);
		if (!paths.length) {
			return;
		}
		const lengths = paths.map((p) => p.getTotalLength());

		// Reduced motion: render every stroke fully drawn, skip animation.
		if (prefersReducedMotion()) {
			paths.forEach((p) => {
				p.style.strokeDasharray = 'none';
				p.style.strokeDashoffset = '0';
			});
			return;
		}

		paths.forEach((p, i) => {
			p.style.strokeDasharray = String(lengths[i]);
			p.style.strokeDashoffset = String(lengths[i]);
		});

		let animRafId: number;
		const timerId = setTimeout(() => {
			const strokeDuration = PARAMS.duration / paths.length;
			const startTime = performance.now();

			const tick = (now: number): void => {
				const elapsed = now - startTime;
				let stillAnimating = false;

				paths.forEach((p, i) => {
					const strokeElapsed = elapsed - i * strokeDuration;
					if (strokeElapsed < 0) {
						p.style.strokeDashoffset = String(lengths[i]);
						stillAnimating = true;
					} else if (strokeElapsed >= strokeDuration) {
						p.style.strokeDashoffset = '0';
					} else {
						const localT = strokeElapsed / strokeDuration;
						const progress = 1 - (1 - localT) ** 2;
						p.style.strokeDashoffset = String(lengths[i] * (1 - progress));
						stillAnimating = true;
					}
				});

				if (stillAnimating) {
					animRafId = requestAnimationFrame(tick);
				}
			};
			animRafId = requestAnimationFrame(tick);
		}, activationDelay);

		return () => {
			clearTimeout(timerId);
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- animRafId may be unset
			if (animRafId) {
				cancelAnimationFrame(animRafId);
			}
		};
	}, [pathData, svgDims, activationDelay]);

	return { containerRef, pathRefs, pathData, svgDims };
}

/**
 * The single "redacted" visual primitive. See file header for the spec.
 */
export const ScribbleBar = ({
	label,
	delay = PARAMS.delay,
}: ScribbleBarProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const accessibleLabel = label ?? formatMessage(messages.redactedTokenA11y);
	const { containerRef, pathRefs, pathData, svgDims } = useScribbleAnimationInternal(delay);
	// Tracked separately: the editor reclaims focus after a click, and that blur
	// must not drop the hover state.
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const isActive = isHovered || isFocused;
	// A pointer press focuses the button too, but that focus must not count as
	// activation: once the pointer leaves, the bar would stay active as if it had
	// been selected. Selection is the editor's job (its node-selection ring is
	// independent of this button) and in a read-only renderer there is nothing to
	// select, so only hover and keyboard focus activate the bar. Set on mousedown
	// (which precedes focus) and cleared once the focus event has been classified.
	const isPointerFocusRef = useRef(false);

	const tooltipContent = (
		<React.Fragment>
			<div>{formatMessage(messages.redactedTooltipLine1)}</div>
			<div>{formatMessage(messages.redactedTooltipLine2)}</div>
		</React.Fragment>
	);

	return (
		<Tooltip content={tooltipContent} position="top">
			{({ ref: tooltipRef, onFocus: onTooltipFocus, onBlur: onTooltipBlur, ...tooltipProps }) => (
				<button
					{...tooltipProps}
					type="button"
					ref={(node: HTMLButtonElement | null) => {
						containerRef.current = node;
						tooltipRef(node);
						// A bar that mounts under the pointer gets no mouseenter.
						if (node?.matches(':hover')) {
							setIsHovered(true);
						}
					}}
					// A native <button> is the correct interactive element for the
					// tooltip trigger: it is keyboard-focusable by default (no
					// `tabIndex` on a non-interactive node), so keyboard users can reach
					// the tooltip (redaction reason / "Powered by Atlassian Guard") and
					// the Guard-icon affordance that pointer users get on hover.
					aria-label={accessibleLabel}
					css={styles.container}
					data-testid="guard-scribble-bar"
					// Tells the editor's inline extension wrapper to drop its background,
					// hover ring and overlay. No effect outside the editor.
					data-inline-extension-layout="flow"
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					onMouseDown={() => {
						isPointerFocusRef.current = true;
					}}
					onMouseUp={() => {
						// Covers a press on an already-focused button, where no focus event fires.
						isPointerFocusRef.current = false;
					}}
					onFocus={(event) => {
						onTooltipFocus(event);
						if (isPointerFocusRef.current) {
							isPointerFocusRef.current = false;
							return;
						}
						setIsFocused(true);
					}}
					onBlur={(event) => {
						onTooltipBlur(event);
						setIsFocused(false);
					}}
				>
					{svgDims && (
						<svg
							css={[styles.svg, isActive && styles.svgActive]}
							width={svgDims.w}
							height={svgDims.h}
							viewBox={`0 0 ${svgDims.w} ${svgDims.h}`}
							data-testid="guard-scribble-strokes"
							aria-hidden="true"
							focusable="false"
						>
							{pathData.map((d, i) => (
								<path
									key={i}
									ref={(el) => {
										pathRefs.current[i] = el;
									}}
									d={d}
									fill="none"
									strokeLinecap="round"
									strokeWidth={EFFECTIVE_STROKE_WIDTH}
								/>
							))}
						</svg>
					)}
					<span css={[styles.icon, isActive && styles.iconActive]} aria-hidden="true">
						<GuardIcon appearance="brand" size="xxsmall" label="" />
					</span>
				</button>
			)}
		</Tooltip>
	);
};

/**
 * Legacy hook retained for backwards compatibility with the 0.2.x published API,
 * which exposed `useScribbleAnimation` returning an SVG `ref`. The draw
 * animation is now fully encapsulated inside {@link ScribbleBar} (see
 * `useScribbleAnimationInternal`), so this hook is no longer needed and new
 * call sites should not use it. It returns a stable `ref` so existing call sites
 * keep compiling; the ref is inert.
 */
export const useScribbleAnimation = (): { ref: React.RefObject<SVGSVGElement> } => {
	// useRef (not useMemo) guarantees a stable wrapper identity across renders.
	const wrapper = useRef<{ ref: React.RefObject<SVGSVGElement> }>({
		ref: React.createRef<SVGSVGElement>(),
	});
	return wrapper.current;
};
